"use client"

import styled from "@emotion/styled"
import { Suspense, useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import {
  ColorCards,
  Container,
  ScrollBarAnchorIcon,
  ScrollBarToolbar,
  useInViewConfig,
  useIsDesktop,
  useTransitionUpStyles,
} from "ui"
import {
  extractProperty,
  getColorFromAnchorPosition,
  scrollToCardByRef,
  useDebouncedCallback,
} from "utils"

const StyledMainContentWrapper = styled.div`
  position: relative;
`
const StyledScrollerWrapper = styled.div<{ $isSticky: boolean }>`
  position: sticky;
  top: 64px;
  z-index: 100004;
  padding: 0 20px;
  padding-top: 11px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  width: 100%;
  margin: 0 auto;
  margin-bottom: 64px;
  ${(props) =>
    props.$isSticky &&
    `
      background-color: white;
      box-shadow: 0px 4px 12px 0px #00000014;
      padding: 8px 20px;
    `}
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 1040px;
    margin-bottom: 68px;
    top: 104px;
    border-radius: 16px;
    ${(props) =>
      props.$isSticky &&
      `
        padding: 16px;
      `}
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 1280px;
  }
`

const StyledScroller = styled.div<{ gradient: string }>`
  position: relative;
  height: 15px;
  border-radius: 11px;
  background: linear-gradient(90deg, ${(props) => props.gradient});
  max-width: 800px;
  margin: 0 auto;
`

const DraggableAnchor = styled.div<{ left: number; locale: string }>`
  position: absolute;
  top: 70%;
  ${(props) => (props.locale === "en" ? `left: ${props.left}px` : `right: ${props.left}px`)};
  transform: ${(props) =>
    props.locale === "en" ? `translate(-50%, -50%)` : `translate(50%, -50%)`};
  width: 40px;
  height: 40px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 52px;
    height: 52px;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  z-index: 2;
  cursor: ew-resize;
  overflow: visible;
  background: transparent;
  transition: left 200ms linear, right 200ms linear;
`

const AnchorTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  width: 17.56px;
  height: 20px;
  svg {
    width: 100%;
    height: 100%;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 36px;
    height: 41px;
  }
`

type Props = {
  data: any
  noTransition?: boolean
  definedMargin?: string
  locale: string
}

function BGColorPattern({ data, noTransition, locale }: Props) {
  const isDesktop = useIsDesktop()
  const { ref: inViewRef, inView } = useInView({
    ...useInViewConfig,
    threshold: 0.1,
    rootMargin: "0px",
  })
  const { ref: inViewScrollerRef, inView: inViewScroller } = useInView({
    threshold: 1,
    rootMargin: isDesktop ? "-140px 0px 0px 0px" : "-90px 0px 0px 0px",
    triggerOnce: false,
  })

  const [selectedColor, setSelectedColor] = useState("")
  const [sliceEndNumber, setSliceEndNumber] = useState(35)
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const selectColorGroup: {
    colorCodeForScroller: string
    colorList: { colorCode: string; colorNumber: string }[]
  }[] = extractProperty(data, "selectColorGroup")?.map((color: any) => {
    const colorCodeForScroller = extractProperty(color, "colorCodeForScroller")
    const colorList = extractProperty(color, "colorList")?.map((colorDetails: any) => {
      const colorCode = extractProperty(colorDetails, "colorCode")
      const colorNumber = extractProperty(colorDetails, "colorNumber")
      return { colorCode, colorNumber }
    })
    return { colorCodeForScroller, colorList }
  })
  const sharingText: string = extractProperty(data, "sharingText")

  const transitionStyles = useTransitionUpStyles(noTransition || inView)

  const everyColorListForCards: {
    colorCode: string
    colorNumber: string
    colorCodeForScroller: string
  }[] = selectColorGroup
    ?.map(
      (colorGroup: {
        colorCodeForScroller: string
        colorList: { colorCode: string; colorNumber: string }[]
      }) => {
        const newObject = colorGroup.colorList?.map(
          (color: { colorCode: string; colorNumber: string }) => {
            return {
              colorCode: color.colorCode,
              colorNumber: color.colorNumber,
              colorCodeForScroller: colorGroup.colorCodeForScroller,
            }
          },
        )
        return newObject
      },
    )
    ?.flat()

  const gradientColorsForScroller = selectColorGroup?.map(
    (color: { colorCodeForScroller: string }) => {
      return color.colorCodeForScroller
    },
  )

  const displayMoreCards = () =>
    setSliceEndNumber((prev) => (prev < everyColorListForCards?.length ? prev + 14 : prev))

  const debouncedOnColorPick = useDebouncedCallback((color: string) => {
    scrollToCardByRef(color, everyColorListForCards, sliceEndNumber, displayMoreCards, cardRefs)
  }, 300)

  const [anchorLeft, setAnchorLeft] = useState(0)

  const changeAnchorState = (color: string, position: number) => {
    setAnchorLeft(position)
    setSelectedColor(color)
  }
  const debounceChangeAnchorState = useDebouncedCallback((color: string, position: number) => {
    changeAnchorState(color, position)
  }, 100)

  const scrollerRef = useRef<HTMLDivElement>(null)

  // handle anchor movement on scroller
  const [isManuallyDragging, setIsManuallyDragging] = useState(false)
  const handlePointerStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsManuallyDragging(true)
    e.preventDefault()

    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX

    const updatePosition = (x: number) => {
      if (!scrollerRef.current) return
      const rect = scrollerRef.current.getBoundingClientRect()
      const newLeft = Math.min(Math.max(x - rect.left, 0), rect.width)

      const color = getColorFromAnchorPosition(x, scrollerRef, gradientColorsForScroller)
      if (color) {
        debouncedOnColorPick(color)
        changeAnchorState(color, newLeft)
      }
    }

    updatePosition(clientX)

    const onPointerMove = (moveEvent: MouseEvent | TouchEvent) => {
      const x =
        "touches" in moveEvent ? moveEvent.touches[0].clientX : (moveEvent as MouseEvent).clientX
      updatePosition(x)
    }

    const onPointerEnd = () => {
      document.removeEventListener("mousemove", onPointerMove as any)
      document.removeEventListener("mouseup", onPointerEnd)
      document.removeEventListener("touchmove", onPointerMove as any)
      document.removeEventListener("touchend", onPointerEnd)
    }

    document.addEventListener("mousemove", onPointerMove as any)
    document.addEventListener("mouseup", onPointerEnd)
    document.addEventListener("touchmove", onPointerMove as any)
    document.addEventListener("touchend", onPointerEnd)
    document.addEventListener("scrollend", () => setIsManuallyDragging(false))
  }

  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!scrollerRef.current || hasInitialized.current) return
    hasInitialized.current = true

    const rect = scrollerRef.current.getBoundingClientRect()
    changeAnchorState(gradientColorsForScroller?.[0], 0)
    const initialX = rect.left + anchorLeft
    const color = getColorFromAnchorPosition(initialX, scrollerRef, gradientColorsForScroller)
    if (color) debouncedOnColorPick(color)

    const triggerMobileScrollForColorChange = (e: Event) => {
      const target = e.target as Node
      const scroller = scrollerRef.current

      if (scroller && (target === scroller || scroller.contains(target))) {
        // Target is the scroller or inside the scroller
        return
      } else {
        //Target is outside the scroller
        setIsManuallyDragging(false)
      }
    }
    document.addEventListener("touchstart", triggerMobileScrollForColorChange)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <StyledMainContentWrapper ref={inViewRef}>
      <StyledScrollerWrapper
        ref={inViewScrollerRef}
        style={inView ? transitionStyles : { opacity: 0, transform: "translateY(0px)" }}
        $isSticky={!inViewScroller}
      >
        <StyledScroller
          gradient={gradientColorsForScroller.map((c: string) => c).join(", ")}
          ref={scrollerRef}
          onMouseDown={handlePointerStart}
          onTouchStart={handlePointerStart}
        >
          <DraggableAnchor left={anchorLeft} style={{ touchAction: "none" }} locale={locale}>
            <AnchorTooltip>
              <ScrollBarToolbar selectedColor={selectedColor} />
            </AnchorTooltip>

            <ScrollBarAnchorIcon style={{ background: "transparent" }} />
          </DraggableAnchor>
        </StyledScroller>
      </StyledScrollerWrapper>
      <Container style={transitionStyles}>
        <Suspense fallback={null}>
          <ColorCards
            data={everyColorListForCards}
            inView={inView}
            isManuallyDragging={isManuallyDragging}
            gradientColorsForScroller={gradientColorsForScroller}
            scrollerRef={scrollerRef}
            debounceChangeAnchorState={debounceChangeAnchorState}
            sliceEndNumber={sliceEndNumber}
            displayMoreCards={displayMoreCards}
            cardRefs={cardRefs}
            sharingText={sharingText}
          />
        </Suspense>
      </Container>
    </StyledMainContentWrapper>
  )
}

export default BGColorPattern
